import { EMPTY_STRING } from '../../constants/Separators'
import {
    AgentDTO,
    AgentResponse,
    CallRecordingIntegration,
    CMEResponse,
    CMEResponseInternal,
    DeviceAdminDTO,
    DeviceResponse,
    DevicesResponse,
    PartitionAdminDTO,
    PartitionAdminResponse,
    PartitionCreationResponse,
    TelSysAdminDTO,
    TelSysCreationResponse,
    TelSysFirstSyncStatusResponse
} from '../../Types/Types'
import {
    agentAdminURL,
    agentsAdminURL,
    agentsURL,
    cmeAdminByIdURL,
    cmeDetailsByIDInternalURL,
    cmeDetailsByIDURL,
    currentUserSettings,
    devicesURL,
    deviceURL,
    deviceGroupMembershipURL,
    deviceLinkedUsersURL,
    agentLinkedUsersURL,
    groupsURL,
    EXTENDED_CACHE_TIME,
    getByUsernameURL,
    callRecIntegrationByGUIDURL,
    getCallRecordingIntegrationURL,
    getClientConfigurationURL,
    getCRMConfigurationURL,
    getCRMTemplate,
    getCRMTypesURL,
    getOAuthCrmConfigurationURL,
    getQueueReferences,
    groupMembershipURL,
    NO_CACHE_TIME,
    partitionBaseURL,
    partitionCreatorURL,
    partitionsBaseURL,
    partitionUpdateURL,
    userMoreInformationURL,
    updateCodeURL,
    updateDirectoryURL,
    telSysCreatorURL,
    telSysFirstSyncURL,
    QueryParams,
    TelSysFields,
    telSysInternalURL,
    telSysUpdateURL,
    USER_DARKMODE_URL,
    USER_FAVOURITES_URL,
    userCopyReportsExecuteURL,
    userCopyReportsReportsURL,
    userCopyReportsTargetsURL,
    userSignInAsURL,
    usersURLByTelSysPartition,
    userURL
} from '../../constants/ApiConstants'
import { COMMA, FALSE, TRUE } from '../../constants/Common'
import { UserMoreInformation } from '../../Types/UsersListTypes'
import { RESTMethods } from '../../constants/REST'
import { LanguageErrors } from '../../../../i18n'
import CLanguageHandler from '../../Languages/LanguageHandler'
import { IsVarEmpty, isStringNotEmpty } from '../../utils/GlobalFunctions'
import { accountForDifferentReportingAccessLevelNames } from '../Mappers/UserMappers'
import { VoiceAppLinkageInfo } from '../../../React/ManageQueues/Types/Types'
import {
    AdminUserBody,
    APIData,
    APIListData,
    ApplicationResponseNotification,
    ApplicationUserResponse,
    CRMTypesResponse,
    JSONResponse,
    User
} from '../Types/Types'
import {
    UserCopyReportsExecuteBody,
    UserCopyReportsListResponse,
    UserCopyReportsReportItem,
    UserCopyReportsTargetUserItem
} from '../../Types/UserCopyReportsTypes'
import { requestGetJSON } from './REST/RESTAPIClient'
import { purgeCacheForURL, requestJSON } from './REST/RESTClient'
import { SidebarMenuItems } from '../../constants/ReactRouter/Header/Constants'
import { ClientConfigurationBackend } from '../../../ReactRouting/Components/Organisms/ClientConfiguration/Modal/Types/Types'
import { ClientConfigurationEntry, ClientConfigurations } from '../../Types/ClientConfigurationTypes'
import {
    CRMServerRequest,
    CRMServerResponse,
    CRMTemplateResponse
} from '../../../ReactRouting/Components/Organisms/CRMConfiguration/Modal/Types/Types'
import { CRMConfigurationEntry, CRMConfigurations } from '../../Types/CRMConfiguration/Types'
import { GroupMembershipResponse } from '../../../React/Common/Types/Types'
import { CRMConfigurationTemplateType } from '../../../ReactRouting/Components/Organisms/CRMConfiguration/Modal/Constants/Constants'
import { OAuthConfig } from '../../../React/Common/Components/OAuthButton/Types/Types'

const isValidCmeID = (cmeID: Nullable<string>): boolean => isStringNotEmpty(cmeID)

/**
 * Builds a rejected promise for invalid CME identifiers using {@link LanguageErrors}.
 */
const rejectInvalidCmeId = (): Promise<never> => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()

    language.loadBundle(LanguageErrors.NAME)

    return Promise.reject(new Error(language.get(LanguageErrors.InvalidCmeId)))
}

export const getCME = (cmeID: string, cacheTime?: number): Promise<Nullable<CMEResponse>> =>
    !isValidCmeID(cmeID)
        ? rejectInvalidCmeId()
        : requestGetJSON<APIData<CMEResponse>>(cmeDetailsByIDURL(cmeID), {
              cacheTime
          }).then((res: JSONResponse<APIData<CMEResponse>>): Nullable<CMEResponse> => res?.Data)

export const getCMEInternal = (cmeID: string): Promise<Nullable<CMEResponseInternal>> =>
    !isValidCmeID(cmeID)
        ? rejectInvalidCmeId()
        : requestGetJSON<CMEResponseInternal>(cmeDetailsByIDInternalURL(cmeID)).then(
              (res: JSONResponse<CMEResponseInternal>): Nullable<CMEResponseInternal> => res
          )

export const getTelSysInternal = (telSysID: string | number): Promise<JSONResponse<TelSysAdminDTO>> =>
    requestGetJSON<APIData<TelSysAdminDTO>>(
        telSysInternalURL.appendPathParams(telSysID).appendQueryParams({
            [QueryParams.ADVANCED_FIELDS_PARAM]: true,
            [QueryParams.TEL_SYS_FIELDS]: [
                TelSysFields.TELSYS_TYPE,
                TelSysFields.LICENSED_FOR_RPT_USER_TYPE,
                TelSysFields.CALL_REC_INT_ID_DEFAULT,
                TelSysFields.E164_ENABLED,
                TelSysFields.INTERNAL_DIGIT_LEN,
                TelSysFields.COUNTRY_CODE,
                TelSysFields.TIME_ZONE,
                TelSysFields.START_OF_DAY,
                TelSysFields.PARTITIONS_RPTING_SCOPE_ALL_ENABLED
            ].join(COMMA)
        })
    ).then((res: JSONResponse<APIData<TelSysAdminDTO>>): JSONResponse<TelSysAdminDTO> => res?.Data)

export const getTelSys = (telSysID: string | number): Promise<JSONResponse<TelSysAdminDTO>> =>
    requestGetJSON<APIData<TelSysAdminDTO>>(telSysUpdateURL(telSysID)).then(
        (res: JSONResponse<APIData<TelSysAdminDTO>>): JSONResponse<TelSysAdminDTO> => res?.Data
    )

export const getTelSysFirstTimeSync = (
    telSysID: string | number
): Promise<JSONResponse<TelSysFirstSyncStatusResponse>> =>
    requestGetJSON<APIData<TelSysFirstSyncStatusResponse>>(telSysFirstSyncURL(telSysID)).then(
        (res: JSONResponse<APIData<TelSysFirstSyncStatusResponse>>): JSONResponse<TelSysFirstSyncStatusResponse> =>
            res?.Data
    )

export const createTelSys = (telSysDTO: TelSysAdminDTO): Promise<JSONResponse<TelSysCreationResponse>> =>
    requestJSON<APIData<TelSysCreationResponse>>(telSysCreatorURL, {
        method: RESTMethods.POST,
        body: telSysDTO
    }).then(
        (res: JSONResponse<APIData<TelSysCreationResponse>>): JSONResponse<TelSysCreationResponse> =>
            res?.Data?.TelSysID
                ? {
                      TelSysID: parseInt(res.Data.TelSysID.toString(), 10)
                  }
                : undefined
    )

export const updateTelSys = (telSysID: string | number, telSysDTO: TelSysAdminDTO): Promise<JSONResponse<void>> =>
    requestJSON<void>(telSysUpdateURL(telSysID), {
        method: RESTMethods.PUT,
        body: telSysDTO
    })

export const deleteTelSys = (telSysID: string | number): Promise<JSONResponse<void>> =>
    requestJSON<void>(telSysUpdateURL(telSysID), {
        method: RESTMethods.DELETE
    })

/**
 * Deletes multiple telephony servers in parallel (same pattern as {@link deleteCRMConfigurations}).
 *
 * @param telSysIDs - TelSys identifiers to delete.
 * @returns Resolves with each API response when all deletes succeed; rejects if any request fails.
 */
export const deleteTelSysMany = (telSysIDs: ReadonlyArray<string | number>): Promise<JSONResponse<void>[]> =>
    Promise.all(telSysIDs.map((id: string | number): Promise<JSONResponse<void>> => deleteTelSys(id)))

/**
 * Deletes a CM Environment via the admin API.
 *
 * @param cmeID - CME identifier (UUID).
 * @returns API response. Rejects if cmeID is null, undefined, empty, or the literal "undefined".
 */
export const deleteCME = (cmeID: string): Promise<JSONResponse<void>> =>
    !isValidCmeID(cmeID)
        ? rejectInvalidCmeId()
        : requestJSON<void>(cmeAdminByIdURL(cmeID), {
              method: RESTMethods.DELETE
          })

/**
 * Deletes multiple CM Environments in parallel.
 *
 * @param cmeIDs - CME identifiers to delete.
 * @returns Resolves when all deletes succeed.
 */
export const deleteCMEMany = (cmeIDs: ReadonlyArray<string>): Promise<JSONResponse<void>[]> =>
    Promise.all(cmeIDs.map((id: string): Promise<JSONResponse<void>> => deleteCME(id)))

/**
 * Deletes an ACD Agent by agent number (admin API path {@code .../acd-agents/{agentNumber}}).
 *
 * @param telSysID - Telephony server ID.
 * @param partIdInTelSys - Partition ID within the telephone system.
 * @param agentNumber - Agent number (ACD agent ID) to delete.
 */
export const deleteACDAgent = (
    telSysID: number,
    partIdInTelSys: number,
    agentNumber: string
): Promise<JSONResponse<void>> =>
    requestJSON<void>(agentAdminURL(telSysID, partIdInTelSys, encodeURIComponent(agentNumber)), {
        method: RESTMethods.DELETE
    })

/**
 * Deletes multiple ACD agents in parallel for the same partition.
 *
 * @param telSysID - Telephony server ID.
 * @param partIdInTelSys - Partition ID within the telephone system.
 * @param agentNumbers - Agent numbers to delete.
 */
export const deleteACDAgentMany = (
    telSysID: number,
    partIdInTelSys: number,
    agentNumbers: ReadonlyArray<string>
): Promise<JSONResponse<void>[]> =>
    Promise.all(
        agentNumbers.map((n: string): Promise<JSONResponse<void>> => deleteACDAgent(telSysID, partIdInTelSys, n))
    )

/**
 * Deletes a call recording integration by its stable GUID (admin API).
 *
 * @param integrationGUID - Call recording integration GUID from list/detail payloads.
 * @returns API response for the DELETE request.
 */
export const deleteCallRecordingIntegration = (integrationGUID: string): Promise<JSONResponse<void>> =>
    requestJSON<void>(callRecIntegrationByGUIDURL(integrationGUID), {
        method: RESTMethods.DELETE
    })

/**
 * Deletes multiple call recording integrations in parallel (same pattern as {@link deleteCMEMany}).
 *
 * @param integrationGUIDs - Integration GUIDs to delete.
 * @returns Resolves when all deletes succeed; rejects if any request fails.
 */
export const deleteCallRecordingIntegrationsMany = (
    integrationGUIDs: ReadonlyArray<string>
): Promise<JSONResponse<void>[]> =>
    Promise.all(
        integrationGUIDs.map((guid: string): Promise<JSONResponse<void>> => deleteCallRecordingIntegration(guid))
    )

/**
 * Deletes a partition-scoped code via the admin codes endpoint.
 *
 * @param telSysID - Telephony server ID.
 * @param partIDInTelSys - Partition ID within the telephone system.
 * @param code - Code identifier path segment.
 * @param codeType - Code type path segment (stringified for the URL).
 * @returns API response for the DELETE request.
 */
export const deleteCode = (
    telSysID: string | number,
    partIDInTelSys: string | number,
    code: string,
    codeType: string | number
): Promise<JSONResponse<void>> =>
    requestJSON<void>(updateCodeURL(`${telSysID}`, `${partIDInTelSys}`, code, `${codeType}`), {
        method: RESTMethods.DELETE
    })

/**
 * Deletes multiple codes in parallel (each row must include TelSys, partition, code, and type).
 *
 * @param rows - Rows with TelSysID, PartIDInTelSys, Code, and CodeType (minimal fields used for the URL).
 * @returns Resolves when all deletes succeed; rejects if any request fails.
 */
export const deleteCodesMany = (
    rows: ReadonlyArray<{
        TelSysID: string | number
        PartIDInTelSys: string | number
        Code: string
        CodeType: string | number
    }>
): Promise<JSONResponse<void>[]> =>
    Promise.all(
        rows.map(
            (row: {
                TelSysID: string | number
                PartIDInTelSys: string | number
                Code: string
                CodeType: string | number
            }): Promise<JSONResponse<void>> => deleteCode(row.TelSysID, row.PartIDInTelSys, row.Code, row.CodeType)
        )
    )

/**
 * Deletes a directory entry identified by TelSys, partition, and telephone number (admin API).
 *
 * @param telSysID - Telephony server ID.
 * @param partIDInTelSys - Partition ID within the telephone system.
 * @param telephoneNumber - Directory entry telephone number (path segment).
 * @returns API response for the DELETE request.
 */
export const deleteDirectoryEntry = (
    telSysID: string | number,
    partIDInTelSys: string | number,
    telephoneNumber: string
): Promise<JSONResponse<void>> =>
    requestJSON<void>(updateDirectoryURL(String(telSysID), String(partIDInTelSys), telephoneNumber), {
        method: RESTMethods.DELETE
    })

/**
 * Deletes multiple directory entries in parallel.
 *
 * @param rows - Rows with TelSysID, PartIDInTelSys, and TelephoneNumber (minimal fields used for the URL).
 * @returns Resolves when all deletes succeed; rejects if any request fails.
 */
export const deleteDirectoryEntriesMany = (
    rows: ReadonlyArray<{
        TelSysID: string | number
        PartIDInTelSys: string | number
        TelephoneNumber: string
    }>
): Promise<JSONResponse<void>[]> =>
    Promise.all(
        rows.map(
            (row: {
                TelSysID: string | number
                PartIDInTelSys: string | number
                TelephoneNumber: string
            }): Promise<JSONResponse<void>> =>
                deleteDirectoryEntry(row.TelSysID, row.PartIDInTelSys, row.TelephoneNumber)
        )
    )

export const getPartition = (
    telSysID: string | number,
    partitionID: string | number
): Promise<JSONResponse<PartitionAdminDTO>> =>
    requestGetJSON<APIData<PartitionAdminDTO>>(partitionUpdateURL(telSysID, partitionID)).then(
        (res: JSONResponse<APIData<PartitionAdminDTO>>): JSONResponse<PartitionAdminDTO> => res?.Data
    )

export const getPartitionForAgents = (
    telSysID: string | number,
    partitionID: string | number,
    nonAdmin?: boolean
): Promise<JSONResponse<PartitionAdminDTO>> =>
    requestGetJSON<APIData<PartitionAdminDTO>>(
        (nonAdmin ? partitionBaseURL : partitionUpdateURL)(telSysID, partitionID)
    ).then((res: JSONResponse<APIData<PartitionAdminDTO>>): JSONResponse<PartitionAdminDTO> => res?.Data)

export const createPartition = (
    telSysID: string | number,
    partitionDTO: PartitionAdminDTO
): Promise<JSONResponse<PartitionCreationResponse>> =>
    requestJSON<APIData<PartitionCreationResponse>>(partitionCreatorURL(telSysID), {
        method: RESTMethods.POST,
        body: partitionDTO
    }).then(
        (res: JSONResponse<APIData<PartitionCreationResponse>>): JSONResponse<PartitionCreationResponse> =>
            res?.Data?.PartIDInTelSys
                ? {
                      PartIDInTelSys: parseInt(res.Data.PartIDInTelSys.toString(), 10)
                  }
                : undefined
    )

export const updatePartition = (
    telSysID: string | number,
    partitionID: string | number,
    partitionDTO: PartitionAdminDTO
): Promise<JSONResponse<PartitionAdminDTO>> =>
    requestJSON<PartitionAdminDTO>(partitionUpdateURL(telSysID, partitionID), {
        method: RESTMethods.PUT,
        body: partitionDTO
    })

/**
 * Deletes one partition.
 *
 * The application users scoped to the partition are deleted with it unless {@code autoDeleteUsers} is false.
 * The value is always sent rather than left to the endpoint's own default, so the caller's intent is explicit
 * on the wire. It is sent as a string because a `false` boolean would be dropped from the query entirely.
 *
 * @param telSysID - Telephony server the partition belongs to.
 * @param partitionID - Partition identifier within that telephony server.
 * @param autoDeleteUsers - Whether to delete the partition's application users along with it.
 * @returns Resolves with the API response, rejects when the server refuses the delete.
 */
export const deletePartition = (
    telSysID: string | number,
    partitionID: string | number,
    autoDeleteUsers: boolean = true
): Promise<JSONResponse<PartitionAdminResponse>> =>
    requestJSON<PartitionAdminResponse>(
        partitionUpdateURL(telSysID, partitionID).appendQueryParams({
            [QueryParams.AUTO_DELETE_USERS]: autoDeleteUsers ? TRUE : FALSE
        }),
        {
            method: RESTMethods.DELETE
        }
    )

/**
 * Outcome of one partition delete within a bulk request.
 */
export type PartitionDeleteOutcome = {
    partitionID: string
    isDeleted: boolean
    error?: Error
}

/**
 * Deletes multiple partitions on one telephony server in parallel.
 *
 * Every delete is attempted and its own outcome reported, because the server refuses individual partitions
 * (for example while the telephone module is in service) and a partial success has to be distinguishable from
 * a total failure — the caller needs to know which rows have gone and which have not.
 *
 * @param telSysID - Telephony server the partitions belong to.
 * @param partitionIDs - Partition identifiers within that telephony server.
 * @param autoDeleteUsers - Whether to delete each partition's application users along with it.
 * @returns Resolves with one outcome per requested partition, in the order requested; never rejects.
 */
export const deletePartitionMany = (
    telSysID: string | number,
    partitionIDs: ReadonlyArray<string | number>,
    autoDeleteUsers: boolean = true
): Promise<PartitionDeleteOutcome[]> =>
    Promise.all(
        partitionIDs.map(
            (partitionID: string | number): Promise<PartitionDeleteOutcome> =>
                deletePartition(telSysID, partitionID, autoDeleteUsers)
                    .then((): PartitionDeleteOutcome => ({ partitionID: String(partitionID), isDeleted: true }))
                    .catch(
                        (error: Error): PartitionDeleteOutcome => ({
                            partitionID: String(partitionID),
                            isDeleted: false,
                            error
                        })
                    )
        )
    )

export const deleteTelSysAndPartitions = (
    telSysID?: Nullable<string | number>,
    partitionID?: Nullable<string | number>
): void => {
    if (!IsVarEmpty(telSysID) && !IsVarEmpty(partitionID)) {
        requestGetJSON<APIData<TelSysAdminDTO>>(telSysUpdateURL(telSysID)).then(
            (telSys: JSONResponse<APIData<TelSysAdminDTO>>): void => {
                if (!IsVarEmpty(telSys) && telSys?.Data) {
                    updateTelSys(telSysID, {
                        ...telSys.Data,
                        CommsEnabled: false
                    }).then((): void => {
                        requestGetJSON<APIData<PartitionAdminDTO>>(partitionUpdateURL(telSysID, partitionID)).then(
                            (partition: JSONResponse<APIData<PartitionAdminDTO>>): void => {
                                if (!IsVarEmpty(partition) && partition?.Data) {
                                    updatePartition(telSysID, partitionID, {
                                        ...partition.Data,
                                        CommsEnabled: false
                                    }).then((): void => {
                                        deleteTelSys(telSysID)
                                    })
                                }
                            }
                        )
                    })
                }
            }
        )
    }
}

export const getPartitions = (telSysID: string | number): Promise<PartitionAdminResponse[]> =>
    requestGetJSON<APIListData<PartitionAdminResponse>>(partitionsBaseURL(telSysID))
        .then(
            (partitions: JSONResponse<APIListData<PartitionAdminResponse>>): PartitionAdminResponse[] =>
                partitions?.Data?.Items ?? []
        )
        .catch((): PartitionAdminResponse[] => [])

export const getDevices = (
    telSysID: string | number,
    partitionID: string | number,
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    searchQuery?: string,
    cacheTime?: number,
    deviceFields?: string[],
    sendFiltersWithComma?: boolean
): Promise<DevicesResponse[]> =>
    requestGetJSON<APIListData<DevicesResponse>>(
        devicesURL(
            telSysID,
            partitionID,
            monitoredOnly,
            deviceTypeFilter,
            deviceFields,
            searchQuery,
            sendFiltersWithComma
        ),
        {
            cacheTime
        }
    )
        .then((devices: JSONResponse<APIListData<DevicesResponse>>): DevicesResponse[] => devices?.Data?.Items ?? [])
        .catch((): DevicesResponse[] => [])

export const getGroupMembers = (
    telSysID: string | number,
    partitionID: string | number,
    groupDeviceNumber: string,
    deviceTypeFilter?: string[],
    searchQuery?: string,
    deviceFields?: string,
    cacheTime?: number,
    scopeGroupHierarchy?: boolean
): Promise<Nullable<GroupMembershipResponse>> =>
    requestGetJSON<APIData<GroupMembershipResponse>>(
        groupMembershipURL(
            telSysID,
            partitionID,
            groupDeviceNumber,
            deviceTypeFilter,
            searchQuery,
            deviceFields,
            undefined,
            undefined,
            scopeGroupHierarchy
        ),
        { cacheTime }
    ).then(
        (devices: JSONResponse<APIData<GroupMembershipResponse>>): Nullable<GroupMembershipResponse> => devices?.Data
    )

/** Row from internal {@code /device/{num}/group} or {@code /acdagent/{id}/group}. */
export type DeviceOrAgentGroupMembershipRow = {
    ID: string
    Description: string
}

/**
 * Loads group devices a device is a member of (internal API).
 *
 * @param telSysID - TelSys scope.
 * @param partitionID - Partition scope.
 * @param deviceNumber - Device number.
 * @param groupTypeFilter - Optional group device type filter ranges.
 * @returns Group rows (ID and description).
 */
export const getDeviceGroupMemberships = (
    telSysID: string | number,
    partitionID: string | number,
    deviceNumber: string,
    groupTypeFilter?: string[]
): Promise<DeviceOrAgentGroupMembershipRow[]> =>
    requestGetJSON<DeviceOrAgentGroupMembershipRow[]>(
        deviceGroupMembershipURL(telSysID, partitionID, deviceNumber, groupTypeFilter),
        {}
    ).then((res: JSONResponse<DeviceOrAgentGroupMembershipRow[]>): DeviceOrAgentGroupMembershipRow[] =>
        Array.isArray(res) ? res : []
    )

/**
 * Loads group devices an ACD agent is a member of (internal API).
 *
 * @param telSysID - TelSys scope.
 * @param partitionID - Partition scope.
 * @param agentID - Agent number in partition.
 * @param groupTypeFilter - Optional group device type filter ranges.
 * @returns Group rows (ID and description).
 */
export const getAgentGroupMemberships = (
    telSysID: string | number,
    partitionID: string | number,
    agentID: string,
    groupTypeFilter?: string[]
): Promise<DeviceOrAgentGroupMembershipRow[]> =>
    requestGetJSON<DeviceOrAgentGroupMembershipRow[]>(
        groupsURL(String(telSysID), String(partitionID), agentID, groupTypeFilter),
        {}
    ).then((res: JSONResponse<DeviceOrAgentGroupMembershipRow[]>): DeviceOrAgentGroupMembershipRow[] =>
        Array.isArray(res) ? res : []
    )

/**
 * Loads user IDs linked to a device (same rules as device list {@code Users}).
 */
export const getLinkedUsersForDevice = (
    telSysID: string | number,
    partitionID: string | number,
    deviceNumber: string
): Promise<string[]> =>
    requestGetJSON<string[]>(deviceLinkedUsersURL(telSysID, partitionID, deviceNumber), {}).then(
        (res: JSONResponse<string[]>): string[] => (Array.isArray(res) ? res : [])
    )

/**
 * Loads user IDs linked to an ACD agent.
 */
export const getLinkedUsersForAgent = (
    telSysID: string | number,
    partitionID: string | number,
    agentID: string
): Promise<string[]> =>
    requestGetJSON<string[]>(agentLinkedUsersURL(telSysID, partitionID, agentID), {}).then(
        (res: JSONResponse<string[]>): string[] => (Array.isArray(res) ? res : [])
    )

export const getAgents = (
    telSysID: string | number,
    partitionID: string | number,
    monitoredOnly: boolean,
    searchQuery?: string,
    cacheTime?: number
): Promise<AgentResponse[]> =>
    requestGetJSON<APIListData<AgentResponse>>(
        agentsURL(telSysID, partitionID, monitoredOnly, undefined, searchQuery),
        { cacheTime }
    )
        .then((agents: JSONResponse<APIListData<AgentResponse>>): AgentResponse[] => agents?.Data?.Items ?? [])
        .catch((): AgentResponse[] => [])

export const getDeviceCount = (
    telSysID: string | number,
    partitionID: string | number,
    deviceFilter?: string[]
): Promise<number> =>
    getDevices(telSysID, partitionID, false, deviceFilter)
        .then((devices: DevicesResponse[]): number => devices.length ?? 0)
        .catch((): number => 0)

/**
 * Get user by username from the user INFO API.
 *
 * @param userName - The username of the user to get.
 * @param cacheTime - The time to cache the response.
 *
 * @returns A promise that resolves to the user.
 */
export const getUser = (userName: string, cacheTime?: number): Promise<JSONResponse<ApplicationUserResponse>> =>
    requestGetJSON<ApplicationUserResponse>(getByUsernameURL(userName), { cacheTime }).then(
        accountForDifferentReportingAccessLevelNames
    )

export const getUsersByTelSysAndPartition = (
    telSysID: number,
    partitionID: number,
    userFields?: string[]
): Promise<JSONResponse<APIListData<User>>> =>
    requestGetJSON(usersURLByTelSysPartition(telSysID, partitionID, userFields))

export const updateUserSettings = (
    settings: ApplicationResponseNotification
): Promise<JSONResponse<ApplicationResponseNotification>> =>
    requestJSON(currentUserSettings, {
        method: RESTMethods.PUT,
        body: settings
    })

export const createUser = (user: AdminUserBody): Promise<JSONResponse<void>> =>
    requestJSON<void>(userURL(), {
        method: RESTMethods.POST,
        body: user
    })

export const updateUser = (userName: string, user: AdminUserBody): Promise<JSONResponse<void>> =>
    requestJSON(getByUsernameURL(userName), {
        method: RESTMethods.PUT,
        body: user
    })

/**
 * Fetches the activity totals shown behind the users list information icon.
 *
 * Resolved per user on demand rather than with every list row, since the live parts (open sessions and the
 * reports they are running) are only worth computing when someone actually looks at a user.
 *
 * @param userName - Application user id.
 * @returns The user's totals.
 */
export const getUserMoreInformation = (userName: string): Promise<JSONResponse<UserMoreInformation>> =>
    requestJSON<UserMoreInformation>(userMoreInformationURL(userName), {
        method: RESTMethods.GET
    })

/**
 * Value sent as the user {@code State} property when restoring a user.
 *
 * Mirrors {@code CUserConst.eUserStatus.iACTIVE} ({@code 0x1}). The server reads the property's presence rather
 * than its content, so the value is documentation for the reader more than instruction for the server.
 */
const USER_STATE_ACTIVE = '1'

/**
 * Deletes an application user.
 *
 * A user holding a licence is moved to the pending deletion state rather than removed, which is what gives the
 * classic page its two-stage delete: deleting again, with {@code permanent} set, is what finally removes them.
 * A user holding no licence is removed outright either way.
 *
 * @param userName - Application user id.
 * @param permanent - True to remove a pending deletion user for good rather than re-marking them.
 * @returns The empty delete response.
 */
export const deleteUser = (userName: string, permanent: boolean = false): Promise<JSONResponse<void>> =>
    requestJSON<void>(
        permanent
            ? getByUsernameURL(userName).appendQueryParams({ [QueryParams.PERMANENT_DELETION]: true })
            : getByUsernameURL(userName),
        {
            method: RESTMethods.DELETE
        }
    )

/**
 * Takes an application user back out of the pending deletion state.
 *
 * Restoring is a user change rather than an operation of its own: supplying {@code State} is what makes the
 * server clear the pending deletion date and status bit, and that path also re-instates everything suspended
 * with the user - their device or agent monitoring, CXI licence and report schedules.
 *
 * @param userName - Application user id.
 * @returns The empty update response.
 */
export const restoreUser = (userName: string): Promise<JSONResponse<void>> =>
    requestJSON<void>(getByUsernameURL(userName), {
        method: RESTMethods.PUT,
        body: { State: USER_STATE_ACTIVE }
    })

export type AdminUserSignInAsRedirectData = {
    RedirectThePageTo?: string
}

/**
 * Admin "sign in as": switches the current HTTP session to the target application user.
 *
 * @param userName - Target application user id.
 * @returns Promise resolving to API data that may include `RedirectThePageTo` (servlet command).
 */
export const adminSignInAsUser = (userName: string): Promise<JSONResponse<APIData<AdminUserSignInAsRedirectData>>> =>
    requestJSON<APIData<AdminUserSignInAsRedirectData>>(userSignInAsURL(userName), {
        method: RESTMethods.POST
    })

/**
 * Fetches reports that may be copied from the given application user (admin copy-reports flow).
 *
 * @param userName - Source application user id.
 * @returns Promise resolving to the API envelope with `Items` and `Total`.
 */
export const getUserCopyReportsReports = (
    userName: string
): Promise<JSONResponse<UserCopyReportsListResponse<UserCopyReportsReportItem>>> =>
    requestGetJSON<UserCopyReportsListResponse<UserCopyReportsReportItem>>(userCopyReportsReportsURL(userName), {
        errorOnNoContent: false,
        cacheTime: NO_CACHE_TIME
    })

/**
 * Fetches target application users in scope for receiving copied reports from the source user.
 *
 * @param userName - Source application user id.
 * @returns Promise resolving to the API envelope with `Items` and `Total`.
 */
export const getUserCopyReportsTargets = (
    userName: string
): Promise<JSONResponse<UserCopyReportsListResponse<UserCopyReportsTargetUserItem>>> =>
    requestGetJSON<UserCopyReportsListResponse<UserCopyReportsTargetUserItem>>(userCopyReportsTargetsURL(userName), {
        errorOnNoContent: false,
        cacheTime: NO_CACHE_TIME
    })

/**
 * Executes copy-reports from the source user to the selected targets.
 *
 * @param userName - Source application user id.
 * @param body - Selected report ids, target usernames, and overwrite flag.
 * @returns Promise resolving when the server completes successfully.
 */
export const executeUserCopyReports = (
    userName: string,
    body: UserCopyReportsExecuteBody
): Promise<JSONResponse<void>> =>
    requestJSON<void>(userCopyReportsExecuteURL(userName), {
        method: RESTMethods.POST,
        body: {
            reportIds: [...body.reportIds],
            targetUsernames: [...body.targetUsernames],
            overwriteTargetReports: body.overwriteTargetReports
        }
    })

export const getCallRecordingIntegration = (
    telSysID: string | number
): Promise<JSONResponse<CallRecordingIntegration[]>> =>
    requestGetJSON<CallRecordingIntegration[]>(getCallRecordingIntegrationURL(telSysID))

export const getAgent = (telSysID: number, partitionID: number, agentNumber: string): Promise<JSONResponse<AgentDTO>> =>
    requestGetJSON<APIData<AgentDTO>>(agentAdminURL(telSysID, partitionID, agentNumber)).then(
        (agents: JSONResponse<APIData<AgentDTO>>): JSONResponse<AgentDTO> => agents?.Data
    )

export const createAgent = (telSysID: number, partitionID: number, agent: AgentDTO): Promise<JSONResponse<void>> =>
    requestJSON<void>(agentsAdminURL(telSysID, partitionID), {
        method: RESTMethods.POST,
        body: agent
    })

export const updateAgent = (
    telSysID: number,
    partitionID: number,
    agentNumber: string,
    agent: AgentDTO
): Promise<JSONResponse<void>> =>
    requestJSON<void>(agentAdminURL(telSysID, partitionID, agentNumber), {
        method: RESTMethods.PUT,
        body: agent
    })

export const getDevice = (
    telSysID: string | number,
    partitionID: string | number,
    deviceNumber: string
): Promise<JSONResponse<APIData<DeviceResponse>>> =>
    requestGetJSON<APIData<DeviceResponse>>(deviceURL(`${telSysID}`, `${partitionID}`, `${deviceNumber}`))

/**
 * <p>Creates a new device under the given TelSys and partition.</p>
 *
 * @param telSysID - Telephony system identifier.
 * @param partitionID - Partition identifier within the TelSys.
 * @param body - Device payload.
 * @returns Server response.
 */
export const createDevice = (
    telSysID: string | number,
    partitionID: string | number,
    body: DeviceAdminDTO
): Promise<JSONResponse<unknown>> =>
    requestJSON(deviceURL(`${telSysID}`, `${partitionID}`, EMPTY_STRING), {
        method: RESTMethods.POST,
        body
    })

/**
 * <p>Updates an existing device identified by device number.</p>
 *
 * @param telSysID - Telephony system identifier.
 * @param partitionID - Partition identifier within the TelSys.
 * @param deviceNumber - Device number to update.
 * @param body - Device payload.
 * @returns Server response.
 */
export const updateDevice = (
    telSysID: string | number,
    partitionID: string | number,
    deviceNumber: string,
    body: DeviceAdminDTO
): Promise<JSONResponse<unknown>> =>
    requestJSON(deviceURL(`${telSysID}`, `${partitionID}`, `${deviceNumber}`), {
        method: RESTMethods.PUT,
        body
    })

export const getUserSettings = (): Promise<JSONResponse<ApplicationResponseNotification>> =>
    requestGetJSON<ApplicationResponseNotification>(currentUserSettings)

export const updateUserFavourites = (favourites: SidebarMenuItems[]): Promise<JSONResponse<void>> =>
    requestJSON(USER_FAVOURITES_URL, {
        method: RESTMethods.POST,
        body: { MenuItemNames: favourites }
    })

export const updateUserDarkmode = (darkMode: boolean): Promise<JSONResponse<void>> =>
    requestJSON(USER_DARKMODE_URL, {
        method: RESTMethods.PUT,
        body: { Activate: darkMode }
    })

export const getDeviceReferences = (
    telsys: string | number,
    partition: string | number,
    idOnTelSysOwner: string
): Promise<JSONResponse<VoiceAppLinkageInfo[]>> =>
    requestGetJSON(getQueueReferences(telsys, partition, idOnTelSysOwner))

/**
 * Add a new client configuration.
 *
 * @param telSysID - {@type number} - Telephony system ID.
 * @param partitionID - {@type number} - Partition ID.
 * @param deviceID - {@type string} - Device ID.
 * @param body - {@type ClientConfigurationBackend} - Client configuration data to add.
 *
 * @return Promise<Nullable<void>> - Resolves to void if successful, or null if not.
 */
export const addClientConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    body: ClientConfigurationBackend
): Promise<Nullable<void>> =>
    requestJSON(getClientConfigurationURL(telSysID, partitionID, deviceID), {
        method: RESTMethods.POST,
        body
    })

/**
 * Change an existing client configuration.
 *
 * @param telSysID - {@type number} - Telephony system ID.
 * @param partitionID - {@type number} - Partition ID.
 * @param deviceID - {@type string} - Device ID.
 * @param id - {@type string} - Client configuration ID.
 * @param body - {@type ClientConfigurationBackend} - Updated client configuration data.
 *
 * @return Promise<Nullable<void>> - Resolves to void if successful, or null if not.
 */
export const changeClientConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    id: string,
    body: ClientConfigurationBackend
): Promise<Nullable<void>> =>
    requestJSON(getClientConfigurationURL(telSysID, partitionID, deviceID, id), {
        method: RESTMethods.PUT,
        body
    })

export const deleteClientConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    id: string
): Promise<Nullable<void>> =>
    requestJSON(getClientConfigurationURL(telSysID, partitionID, deviceID, id), {
        method: RESTMethods.DELETE
    })

/**
 * Delete a client configuration by its ID.
 *
 * @param configs - client configurations to delete.
 *
 * @return Promise<Nullable<void>> - Resolves to void if successful, or null if not.
 */
export const deleteClientConfigurations = (configs: ClientConfigurations): Promise<Nullable<void>[]> =>
    Promise.all(
        configs.map(
            (config: ClientConfigurationEntry): Promise<Nullable<void>> =>
                deleteClientConfiguration(config.TelSysID, config.PartitionID, config.DeviceNumber, config.ID)
        )
    )

export const getCRMConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    id: string
): Promise<JSONResponse<APIData<CRMServerResponse>>> =>
    requestGetJSON(getCRMConfigurationURL(telSysID, partitionID, deviceID, id))

export const getCRMOAuthConfiguration = (crmConfigId: string): Promise<JSONResponse<OAuthConfig>> =>
    requestGetJSON(getOAuthCrmConfigurationURL(crmConfigId), {
        cacheTime: EXTENDED_CACHE_TIME
    })

const purgeCRMOAuthConfigurationCache = (crmConfigId: string): void => {
    purgeCacheForURL(getOAuthCrmConfigurationURL(crmConfigId).constructURL())
}

export const addCRMConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    body: CRMServerRequest
): Promise<Nullable<void>> =>
    requestJSON(getCRMConfigurationURL(telSysID, partitionID, deviceID), {
        method: RESTMethods.POST,
        body
    })

export const changeCRMConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    id: string,
    body: Partial<CRMServerRequest>
): Promise<Nullable<void>> =>
    requestJSON(getCRMConfigurationURL(telSysID, partitionID, deviceID, id), {
        method: RESTMethods.PUT,
        body
    }).then(() => {
        if (body.ConfigurationType === CRMConfigurationTemplateType.Cloud) {
            purgeCRMOAuthConfigurationCache(id)
        }
    })

export const deleteCRMConfiguration = (
    telSysID: number,
    partitionID: number,
    deviceID: string,
    id: string
): Promise<Nullable<void>> =>
    requestJSON(getCRMConfigurationURL(telSysID, partitionID, deviceID, id), {
        method: RESTMethods.DELETE
    })

export const deleteCRMConfigurations = (configs: CRMConfigurations): Promise<Nullable<void>[]> =>
    Promise.all(
        configs.map(
            (config: CRMConfigurationEntry): Promise<Nullable<void>> =>
                deleteCRMConfiguration(
                    config.TelSysID,
                    config.PartIDInTelSys,
                    config.DeviceNumber,
                    config.CRMConfigurationID
                )
        )
    )

export const getCRMTypesForTemplateType = (
    cmeID?: string,
    telSysID?: string,
    templateType?: CRMConfigurationTemplateType
): Promise<JSONResponse<CRMTypesResponse[]>> =>
    requestGetJSON<CRMTypesResponse[]>(getCRMTypesURL(cmeID, telSysID, templateType))

export const getCRMTypesForTemplateTypes = async (
    cmeID: string,
    telSysID: string,
    templateTypes: CRMConfigurationTemplateType[]
): Promise<Record<CRMConfigurationTemplateType, JSONResponse<CRMTypesResponse[]>>[]> =>
    Promise.all(
        templateTypes.map(
            async (
                templateType: CRMConfigurationTemplateType
            ): Promise<Record<string, JSONResponse<CRMTypesResponse[]>>> => ({
                [templateType]: await getCRMTypesForTemplateType(cmeID, telSysID, templateType)
            })
        )
    )

export const getCRMConfigTemplate = (
    crm: string,
    templateType: CRMConfigurationTemplateType
): Promise<JSONResponse<CRMTemplateResponse>> =>
    requestGetJSON<JSONResponse<CRMTemplateResponse>>(getCRMTemplate(crm, templateType))
